/**
 * Device / platform metadata for the running app.
 *
 * Collected at sign-in so the server can label the session (which device,
 * which OS, which app build). Dependency-free: platform comes from the
 * already-installed `@capacitor/core` (works on web, iOS, and Android), the
 * device id is an app-generated UUID persisted in `localStorage`, and the OS
 * version / model are parsed best-effort from the user-agent.
 *
 * If richer native fidelity is ever needed (exact `osVersion`, manufacturer,
 * a vendor device id), add `@capacitor/device` and swap the internals of
 * {@link getDeviceInfo} — the public shape can stay the same.
 *
 * This module also answers "is this a mobile device?" — see
 * {@link classifyDevice}. That verdict drives the boot-time mobile-only wall
 * (`src/pwa/mobile-gate.ts`).
 */

import { Capacitor } from '@capacitor/core';
import { APP_VERSION } from '@/utils/env';

const DEVICE_ID_STORAGE = 'syntry_device_id';

export interface DeviceInfo {
  /** Stable, app-generated identifier for this install / browser. */
  deviceId: string;
  /** Runtime platform reported by Capacitor: `'web' | 'ios' | 'android'`. */
  platform: string;
  /** True inside a native (Capacitor) shell, false in a plain browser. */
  isNative: boolean;
  /** Best-effort OS / platform version — `'17.4'`, `'14'`, `'Windows 10'`, … */
  osVersion: string;
  /** Best-effort device or browser model — `'iPhone'`, `'Pixel 7'`, `'Chrome 120'`, … */
  model: string;
  /** Syntry app version (from `package.json`, injected at build time). */
  appVersion: string;
  /** Raw user-agent string. */
  userAgent: string;
}

/** Generate a UUID, preferring Web Crypto and degrading gracefully. */
function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
      bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
      return (
        `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-` +
        `${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
      );
    }
  } catch {
    /* fall through to the non-crypto fallback */
  }
  // Last resort (no Web Crypto): not cryptographically strong, but unique
  // enough to label a device/session.
  return `dev-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * The persistent device id, created on first use and reused thereafter.
 * Survives sign-out (it identifies the device, not the session) and is only
 * regenerated if `localStorage` is wiped or unavailable.
 */
export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE);
    if (existing) return existing;
  } catch {
    /* storage unavailable (private mode) — fall through to an ephemeral id */
  }

  const id = generateId();
  try {
    localStorage.setItem(DEVICE_ID_STORAGE, id);
  } catch {
    /* not persisted; this id lasts for the current page session only */
  }
  return id;
}

/** Best-effort OS version + device/browser model parsed from a user-agent. */
function parseUserAgent(ua: string, platform: string): { osVersion: string; model: string } {
  if (!ua) return { osVersion: '', model: '' };

  // iOS / iPadOS — native shell or mobile Safari ("... OS 17_4_1 like Mac ...").
  if (platform === 'ios' || /iPhone|iPad|iPod/.test(ua)) {
    const raw = ua.match(/OS (\d+[_.]\d+(?:[_.]\d+)?)/)?.[1];
    // iPadOS sends a desktop-class UA with no device token; when Capacitor
    // reports 'ios' but the UA names none, it can only be an iPad. (We don't
    // fall back to the "Mac OS X 10_15_7" version here — iPadOS freezes that
    // token at Catalina, so it would report a misleading OS version.)
    const model = /iPad/.test(ua)
      ? 'iPad'
      : /iPod/.test(ua)
        ? 'iPod'
        : /iPhone/.test(ua)
          ? 'iPhone'
          : platform === 'ios'
            ? 'iPad'
            : 'iPhone';
    return { osVersion: raw ? raw.replace(/_/g, '.') : '', model };
  }

  // Android — native shell or mobile Chrome ("Linux; Android 14; Pixel 7 Build/...").
  if (platform === 'android' || /Android/.test(ua)) {
    const osVersion = ua.match(/Android (\d+(?:\.\d+)*)/)?.[1] ?? '';
    const model = ua.match(/;\s*([^;)]+?)\s*(?:Build\/|\))/)?.[1]?.trim() || 'Android';
    return { osVersion, model };
  }

  // Desktop / other web.
  return { osVersion: parseDesktopOs(ua), model: parseBrowser(ua) };
}

/** Readable desktop OS label (name + version) from a user-agent. */
function parseDesktopOs(ua: string): string {
  let m: RegExpMatchArray | null;
  if ((m = ua.match(/Windows NT (\d+\.\d+)/))) {
    // NT 10.0 covers both Windows 10 and 11 — the UA can't tell them apart.
    const ver = m[1] ?? '';
    const map: Record<string, string> = { '10.0': '10', '6.3': '8.1', '6.2': '8', '6.1': '7' };
    return `Windows ${map[ver] ?? ver}`;
  }
  if ((m = ua.match(/Mac OS X (\d+[_.]\d+(?:[_.]\d+)?)/))) {
    return `macOS ${(m[1] ?? '').replace(/_/g, '.')}`;
  }
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/.test(ua)) return 'Linux';
  return '';
}

/** Readable browser label (name + major version) from a user-agent. */
function parseBrowser(ua: string): string {
  let m: RegExpMatchArray | null;
  // Edge and Opera embed "Chrome" in their UA, so test them first.
  if ((m = ua.match(/Edg\/(\d+)/))) return `Edge ${m[1]}`;
  if ((m = ua.match(/OPR\/(\d+)/))) return `Opera ${m[1]}`;
  if ((m = ua.match(/Firefox\/(\d+)/))) return `Firefox ${m[1]}`;
  if ((m = ua.match(/Chrome\/(\d+)/))) return `Chrome ${m[1]}`;
  if ((m = ua.match(/Version\/(\d+)[.\d]* Safari/))) return `Safari ${m[1]}`;
  return 'Browser';
}

/** Snapshot the current device / platform metadata. */
export function getDeviceInfo(): DeviceInfo {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const { osVersion, model } = parseUserAgent(ua, platform);
  return {
    deviceId: getDeviceId(),
    platform,
    isNative,
    osVersion,
    model,
    appVersion: APP_VERSION,
    userAgent: ua,
  };
}

/** Human-readable session label, e.g. `"iPhone · iOS 17.4 · Syntry 0.1.0"`. */
export function describeDevice(info: DeviceInfo): string {
  const segments: string[] = [];
  if (info.model) segments.push(info.model);
  if (info.platform === 'ios' || info.platform === 'android') {
    const name = info.platform === 'ios' ? 'iOS' : 'Android';
    segments.push(info.osVersion ? `${name} ${info.osVersion}` : name);
  } else if (info.osVersion) {
    segments.push(info.osVersion); // already includes the OS name on web
  } else {
    segments.push('Web');
  }
  segments.push(`Syntry ${info.appVersion}`);
  return segments.join(' · ');
}

/**
 * Short label for a user-agent recorded ELSEWHERE — e.g. the browser an
 * activity-log entry came from: `"iPhone · iOS 18.5"`, `"Pixel 7 · Android 14"`,
 * `"Chrome 151 · Windows 10"`. Same best-effort parsers as {@link getDeviceInfo}.
 * Returns '' when nothing recognisable is in the agent.
 */
export function describeUserAgent(ua: string): string {
  const text = ua.trim();
  if (!text) return '';

  const { osVersion, model } = parseUserAgent(text, '');
  if (/iPhone|iPad|iPod/.test(text)) return [model, osVersion ? `iOS ${osVersion}` : 'iOS'].join(' · ');
  if (/Android/.test(text)) {
    // Chrome's reduced UA freezes every phone to "Android 10; K" — neither the
    // model nor the version is real, so name the browser instead.
    if (model === 'K') {
      const browser = parseBrowser(text);
      return [browser === 'Browser' ? '' : browser, 'Android'].filter(Boolean).join(' · ');
    }
    return [model, osVersion ? `Android ${osVersion}` : 'Android'].join(' · ');
  }

  // parseBrowser() answers the generic 'Browser' when it recognises nothing, and
  // reduced UAs freeze macOS at 10.15.7 whatever the real version is.
  const os = osVersion === 'macOS 10.15.7' ? 'macOS' : osVersion;
  return [model === 'Browser' ? '' : model, os].filter(Boolean).join(' · ');
}

/* ── Form-factor classification (mobile vs desktop) ─────────────────────── */

/**
 * Low-entropy UA Client Hints. Chromium 90+ only, and only in secure
 * contexts (https:// and http://localhost) — undefined in Safari, Firefox,
 * every iOS browser and on an http://<lan-ip> dev URL. Not in TS 5.6
 * `lib.dom`, hence the local shape.
 */
interface NavigatorUAData {
  readonly mobile: boolean;
  readonly platform: string;
}

export type FormFactor = 'mobile' | 'desktop';

/** Which step of {@link classifyDevice} decided the verdict — for diagnostics. */
export type FormFactorReason =
  | 'no-dom'
  | 'native'
  | 'ua-ch'
  | 'ua-ch-desktop'
  | 'ua-ios'
  | 'ipad-desktop-ua'
  | 'ua-token'
  | 'ua-desktop'
  | 'touch-first'
  | 'pointer-first'
  | 'unknown';

export interface DeviceClassification {
  formFactor: FormFactor;
  reason: FormFactorReason;
}

/** `userAgentData.platform` values that are unambiguously a mobile OS. */
const UA_CH_MOBILE_PLATFORMS: ReadonlySet<string> = new Set(['Android', 'iOS']);

/**
 * `userAgentData.platform` values that are unambiguously a desktop OS. No
 * touch override here on purpose: a Surface in tablet posture or a Chromebook
 * in tablet mode is still a desktop-OS machine, and the platform hint also
 * beats a UA-string-spoofing extension. 'Linux' is deliberately absent —
 * Android's "Request desktop site" (and Samsung DeX, and premium tablets that
 * default to a desktop UA) report platform 'Linux', so it is ambiguous.
 */
const UA_CH_DESKTOP_PLATFORMS: ReadonlySet<string> = new Set([
  'Windows',
  'macOS',
  'Chrome OS',
  'Chromium OS',
  'Fuchsia',
]);

/**
 * Mobile OS / form-factor tokens. `Mobi` covers `Mobile` and `Opera Mobi`;
 * `Tablet` is Firefox's tablet marker. Must be tested BEFORE the desktop
 * tokens: Firefox Android carries no `Linux` token, but a Windows Phone UA
 * would otherwise be caught by nothing. No `Tizen`/`Web0S` — those are TVs.
 */
const UA_MOBILE_TOKENS =
  /Android|Mobi|Tablet|Windows Phone|IEMobile|BlackBerry|BB10|KaiOS|HarmonyOS|Opera Mini/i;

/** Desktop OS tokens (Linux/X11 is intentionally NOT here — see above). */
const UA_DESKTOP_TOKENS = /Windows NT|Macintosh|CrOS/;

function mediaMatches(query: string): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * True for iPhone / iPad / iPod user agents, INCLUDING iPadOS 13+ Safari,
 * which sends a `Macintosh` desktop UA by default. The cheapest reliable
 * discriminator from a real Mac is `navigator.maxTouchPoints` (iPad 5,
 * Mac 0). Chrome DevTools fakes `maxTouchPoints = 1` for its iPad presets,
 * so a coarse primary pointer is accepted as the alternative tell — a real
 * Mac never reports `(pointer: coarse)`. Also matches iPhone "Request
 * Desktop Website" and visionOS Safari, both of which are touch devices.
 */
export function isAppleTouchUa(ua: string, maxTouchPoints: number, pointerCoarse: boolean): boolean {
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/.test(ua) && (maxTouchPoints > 1 || pointerCoarse);
}

let cachedClassification: DeviceClassification | null = null;

/**
 * Classify the runtime as a mobile device or a desktop browser.
 *
 * Synchronous, evaluated ONCE per page load (memoized), no network, and —
 * deliberately — no viewport width: width is a window property, not a device
 * property (a large phone in landscape is 950+ CSS px wide; a desktop window
 * can be narrowed), and a verdict that flapped on resize/rotate would be
 * worse than a wrong one. Only positive evidence for either side decides;
 * a genuinely unknown runtime falls open to "mobile" so an exotic phone
 * browser is never locked out.
 *
 * Precedence:
 *  1. No DOM                                       → mobile (nothing to gate)
 *  2. Capacitor native shell                       → mobile (it IS the app)
 *  3. UA Client Hints (Chromium, secure contexts)  → mobile if `mobile` or
 *     platform Android/iOS; desktop if platform Windows/macOS/Chrome OS;
 *     'Linux'/'Unknown'/absent falls through
 *  4. UA string + touch                            → iPhone/iPad/iPod; iPadOS
 *     desktop UA (Macintosh + touch); mobile OS tokens; then desktop OS tokens
 *  5. Ambiguous OS (Linux/X11/unknown)             → coarse primary pointer +
 *     touch points = mobile; fine pointer or hover = desktop; else mobile
 *
 * Step 5 never requires `(hover: none)`: Samsung/OnePlus phones on Chromium
 * < 149 and Samsung Internet report `hover: hover` for a virtual mouse.
 */
export function classifyDevice(): DeviceClassification {
  if (cachedClassification) return cachedClassification;
  cachedClassification = computeClassification();
  return cachedClassification;
}

/** True unless {@link classifyDevice} explicitly says "desktop". */
export function isMobileDevice(): boolean {
  return classifyDevice().formFactor !== 'desktop';
}

function computeClassification(): DeviceClassification {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { formFactor: 'mobile', reason: 'no-dom' };
  }
  if (Capacitor.isNativePlatform()) {
    return { formFactor: 'mobile', reason: 'native' };
  }

  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (uaData) {
    if (uaData.mobile === true || UA_CH_MOBILE_PLATFORMS.has(uaData.platform)) {
      return { formFactor: 'mobile', reason: 'ua-ch' };
    }
    if (UA_CH_DESKTOP_PLATFORMS.has(uaData.platform)) {
      return { formFactor: 'desktop', reason: 'ua-ch-desktop' };
    }
    // 'Linux' / 'Unknown' → ambiguous, keep going.
  }

  const ua = typeof navigator.userAgent === 'string' ? navigator.userAgent : '';
  const touchPoints = typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : 0;
  const pointerCoarse = mediaMatches('(pointer: coarse)');

  if (/iPhone|iPad|iPod/i.test(ua)) {
    return { formFactor: 'mobile', reason: 'ua-ios' };
  }
  if (/Macintosh/.test(ua) && isAppleTouchUa(ua, touchPoints, pointerCoarse)) {
    return { formFactor: 'mobile', reason: 'ipad-desktop-ua' };
  }
  if (UA_MOBILE_TOKENS.test(ua)) {
    return { formFactor: 'mobile', reason: 'ua-token' };
  }
  if (UA_DESKTOP_TOKENS.test(ua)) {
    return { formFactor: 'desktop', reason: 'ua-desktop' };
  }

  // Ambiguous OS: Android "Request desktop site", DeX, premium Android
  // tablets with a desktop UA, Firefox Android desktop mode, bare Linux.
  if (pointerCoarse && touchPoints > 0) {
    return { formFactor: 'mobile', reason: 'touch-first' };
  }
  if (mediaMatches('(pointer: fine)') || mediaMatches('(hover: hover)')) {
    return { formFactor: 'desktop', reason: 'pointer-first' };
  }
  return { formFactor: 'mobile', reason: 'unknown' };
}

/**
 * Device metadata as request-body fields for `auth.login()` / `auth.mfa()`.
 * `device_name` is the SDK-documented label; the rest ride the request's
 * "additional fields the server may accept" index signature. Empty values are
 * left `undefined` so they drop out of the serialized JSON body.
 */
export function deviceLoginFields(): Record<string, unknown> {
  const info = getDeviceInfo();
  return {
    device_name: describeDevice(info),
    device_id: info.deviceId,
    platform: info.platform,
    os_version: info.osVersion || undefined,
    device_model: info.model || undefined,
    app_version: info.appVersion,
    user_agent: info.userAgent || undefined,
  };
}
